import { useMemo, useState } from 'react';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  Search,
  X
} from 'lucide-react';
import {
  endOfMonth,
  format,
  startOfMonth,
  startOfYear,
  subMonths
} from 'date-fns';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type {
  LoanDataExportCandidateFilters,
  LoanDataExportFilterOption,
  LoanDataExportFilterOptions
} from '@/types/loan-data-export';
import { getBusinessDateString, parseAsLocalDate, toLocalDateString } from '@/utils/dateHelpers';
import {
  countLoanDataExportFilterGroups,
  createDefaultLoanDataExportFilters,
  loanDataExportDateFieldLabel
} from './loanDataExportFilterModel';

interface LoanCohortFiltersProps {
  filters: LoanDataExportCandidateFilters;
  options: LoanDataExportFilterOptions | null;
  loadingOptions: boolean;
  optionsError: string | null;
  onChange: (filters: LoanDataExportCandidateFilters) => void;
  onPasteLoanNumbers?: () => void;
}

type ArrayFilterKey =
  | 'productCategories'
  | 'investors'
  | 'folders'
  | 'purposeCategories'
  | 'purposes'
  | 'programs'
  | 'channelCategories'
  | 'loanOfficers'
  | 'statuses'
  | 'states'
  | 'nonQmDocTypes'
  | 'lienPositions'
  | 'rateTypes'
  | 'ausDecisions';

type FilterKey = ArrayFilterKey | 'date' | 'amount';

interface FilterDefinition {
  key: FilterKey;
  label: string;
}

interface FacetDefinition extends FilterDefinition {
  key: ArrayFilterKey;
  emptyMessage: string;
}

const FACET_DEFINITIONS: FacetDefinition[] = [
  { key: 'productCategories', label: 'Loan type', emptyMessage: 'No loan types found.' },
  { key: 'ausDecisions', label: 'AUS decision', emptyMessage: 'No AUS decisions found.' },
  { key: 'investors', label: 'Investor', emptyMessage: 'No investors found.' },
  { key: 'folders', label: 'Folder', emptyMessage: 'No folders found.' },
  { key: 'purposeCategories', label: 'Purpose', emptyMessage: 'No purposes found.' },
  { key: 'purposes', label: 'Purpose detail', emptyMessage: 'No purpose details found.' },
  { key: 'programs', label: 'Program', emptyMessage: 'No programs found.' },
  { key: 'channelCategories', label: 'Channel', emptyMessage: 'No channels found.' },
  { key: 'loanOfficers', label: 'Loan officer', emptyMessage: 'No loan officers found.' },
  { key: 'statuses', label: 'Loan status', emptyMessage: 'No statuses found.' },
  { key: 'states', label: 'Property state', emptyMessage: 'No states found.' },
  { key: 'nonQmDocTypes', label: 'Non-QM doc type', emptyMessage: 'No document types found.' },
  { key: 'lienPositions', label: 'Lien position', emptyMessage: 'No lien positions found.' },
  { key: 'rateTypes', label: 'Rate type', emptyMessage: 'No rate types found.' }
];

const FILTER_DEFINITIONS: FilterDefinition[] = [
  { key: 'date', label: 'Funded period' },
  ...[
    'productCategories',
    'ausDecisions',
    'investors',
    'folders',
    'purposeCategories',
    'programs',
    'loanOfficers',
    'states'
  ].map((key) => FACET_DEFINITIONS.find((definition) => definition.key === key as ArrayFilterKey)),
  { key: 'amount', label: 'Loan amount' },
  ...[
    'purposes',
    'channelCategories',
    'statuses',
    'nonQmDocTypes',
    'lienPositions',
    'rateTypes'
  ].map((key) => FACET_DEFINITIONS.find((definition) => definition.key === key as ArrayFilterKey))
].filter((definition): definition is FilterDefinition => Boolean(definition));

const MONTHS = Array.from({ length: 12 }, (_, index) => new Date(2020, index, 1));

function validDate(value: string): Date | null {
  if (!value) {
    return null;
  }
  const parsed = parseAsLocalDate(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatAmount(value: string): string {
  const amount = Number(value.replace(/[^0-9.]/g, ''));
  return Number.isFinite(amount) && amount > 0
    ? new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount)
    : value;
}

function FacetValuePanel({
  definition,
  options,
  selected,
  onChange
}: {
  definition: FacetDefinition;
  options: LoanDataExportFilterOption[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const toggle = (value: string) => {
    onChange(selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value]);
  };

  if (options.length === 0) {
    return <div className="px-3 py-6 text-center text-[13px] text-muted-foreground">{definition.emptyMessage}</div>;
  }

  return (
    <div className="max-h-[290px] overflow-auto p-[5px]" role="listbox" aria-label={definition.label} aria-multiselectable="true">
      {options.map((option) => {
        const isSelected = selected.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            role="option"
            aria-selected={isSelected}
            className="flex w-full items-center gap-[9px] rounded-[7px] border-0 bg-transparent px-2 py-1.5 text-left text-[13px] text-foreground outline-none hover:bg-accent focus-visible:bg-accent focus-visible:ring-2 focus-visible:ring-ring [line-height:normal]"
            onClick={() => toggle(option.value)}
          >
            <span className={`inline-flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-[4px] border text-[10px] ${isSelected ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-background'}`}>
              {isSelected ? <Check className="h-2.5 w-2.5" /> : null}
            </span>
            <span className="min-w-0 flex-1 truncate" title={option.label}>{option.label}</span>
            <span className="text-[11.5px] tabular-nums text-muted-foreground">{option.count.toLocaleString()}</span>
          </button>
        );
      })}
    </div>
  );
}

function DateValuePanel({
  filters,
  onChange
}: {
  filters: LoanDataExportCandidateFilters;
  onChange: (filters: LoanDataExportCandidateFilters) => void;
}) {
  const today = validDate(getBusinessDateString()) ?? new Date();
  const initialDate = validDate(filters.dateFrom) ?? today;
  const [year, setYear] = useState(initialDate.getFullYear());
  const [rangeAnchor, setRangeAnchor] = useState<Date | null>(null);
  const from = validDate(filters.dateFrom);
  const to = validDate(filters.dateTo);

  const setRange = (rangeFrom: Date, rangeTo: Date) => {
    onChange({
      ...filters,
      dateFrom: toLocalDateString(rangeFrom),
      dateTo: toLocalDateString(rangeTo)
    });
  };

  const chooseMonth = (monthIndex: number) => {
    const chosen = new Date(year, monthIndex, 1);
    if (!rangeAnchor) {
      setRange(startOfMonth(chosen), endOfMonth(chosen));
      setRangeAnchor(chosen);
      return;
    }
    const first = rangeAnchor.getTime() <= chosen.getTime() ? rangeAnchor : chosen;
    const last = rangeAnchor.getTime() <= chosen.getTime() ? chosen : rangeAnchor;
    setRange(startOfMonth(first), endOfMonth(last));
    setRangeAnchor(null);
  };

  const allYearSelected = Boolean(
    from
    && to
    && from.getFullYear() === year
    && from.getMonth() === 0
    && to.getFullYear() === year
    && to.getMonth() === 11
  );

  return (
    <div className="p-2.5">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="inline-flex h-6 w-6 items-center justify-center rounded-[7px] border bg-background text-primary outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`Show ${year - 1}`}
          onClick={() => setYear((current) => current - 1)}
        >
          <ChevronLeft className="h-[13px] w-[13px]" />
        </button>
        <div className="text-center [line-height:1.25]">
          <div className="text-sm font-bold leading-[18px] tabular-nums">{year}</div>
          <div className="text-[11px] text-muted-foreground">Choose a contiguous range</div>
        </div>
        <button
          type="button"
          className="inline-flex h-6 w-6 items-center justify-center rounded-[7px] border bg-background text-primary outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:text-muted-foreground/40 disabled:hover:bg-background"
          aria-label={`Show ${year + 1}`}
          disabled={year >= today.getFullYear()}
          onClick={() => setYear((current) => current + 1)}
        >
          <ChevronRight className="h-[13px] w-[13px]" />
        </button>
      </div>

      <div className="mt-[9px] grid grid-cols-4 gap-[5px]">
        {MONTHS.map((month, monthIndex) => {
          const monthStart = new Date(year, monthIndex, 1);
          const monthEnd = endOfMonth(monthStart);
          const isSelected = Boolean(from && to && monthEnd >= from && monthStart <= to);
          return (
            <button
              key={monthIndex}
              type="button"
              className={`flex h-11 flex-col items-center justify-center gap-px rounded-lg border text-[12.5px] font-semibold outline-none focus-visible:ring-2 focus-visible:ring-ring ${isSelected ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground hover:bg-accent'}`}
              aria-pressed={isSelected}
              onClick={() => chooseMonth(monthIndex)}
            >
              {format(month, 'MMM')}
              <span className="text-[10.5px] font-medium opacity-70" aria-hidden="true">&nbsp;</span>
            </button>
          );
        })}
      </div>

      <div className="mt-2.5 flex flex-wrap gap-[5px] border-t border-dashed pt-[9px]">
        <button
          type="button"
          className="h-[26px] rounded-[7px] border border-primary bg-primary/[0.07] px-[9px] text-xs font-semibold text-primary outline-none hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => {
            if (allYearSelected) {
              onChange({ ...filters, dateFrom: '', dateTo: '' });
            } else {
              setRange(startOfYear(new Date(year, 0, 1)), endOfMonth(new Date(year, 11, 1)));
            }
            setRangeAnchor(null);
          }}
        >
          {allYearSelected ? `Clear ${year}` : `All of ${year}`}
        </button>
        <button
          type="button"
          className="h-[26px] rounded-[7px] border bg-background px-[9px] text-xs font-medium text-primary outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => {
            const start = subMonths(startOfMonth(today), 2);
            setRange(start, endOfMonth(today));
            setRangeAnchor(null);
          }}
        >
          Last 3 months
        </button>
        <button
          type="button"
          className="h-[26px] rounded-[7px] border bg-background px-[9px] text-xs font-medium text-primary outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => {
            setRange(startOfYear(today), today);
            setRangeAnchor(null);
          }}
        >
          {today.getFullYear()} to date
        </button>
        <button
          type="button"
          className="h-[26px] rounded-[7px] border bg-background px-[9px] text-xs font-medium text-primary outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => {
            onChange({ ...filters, dateFrom: '', dateTo: '' });
            setRangeAnchor(null);
          }}
        >
          All time
        </button>
      </div>

    </div>
  );
}

function AmountValuePanel({
  filters,
  onChange
}: {
  filters: LoanDataExportCandidateFilters;
  onChange: (filters: LoanDataExportCandidateFilters) => void;
}) {
  return (
    <div className="flex gap-[7px] p-2.5">
      <input
        type="text"
        inputMode="decimal"
        value={filters.minAmount}
        placeholder="Min"
        aria-label="Minimum loan amount"
        className="h-10 min-w-0 flex-1 rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20"
        onChange={(event) => onChange({ ...filters, minAmount: event.target.value })}
      />
      <input
        type="text"
        inputMode="decimal"
        value={filters.maxAmount}
        placeholder="Max"
        aria-label="Maximum loan amount"
        className="h-10 min-w-0 flex-1 rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/20"
        onChange={(event) => onChange({ ...filters, maxAmount: event.target.value })}
      />
    </div>
  );
}

function FilterValuePanel({
  filterKey,
  filters,
  options,
  onChange
}: {
  filterKey: FilterKey;
  filters: LoanDataExportCandidateFilters;
  options: LoanDataExportFilterOptions | null;
  onChange: (filters: LoanDataExportCandidateFilters) => void;
}) {
  if (filterKey === 'date') {
    return <DateValuePanel filters={filters} onChange={onChange} />;
  }
  if (filterKey === 'amount') {
    return <AmountValuePanel filters={filters} onChange={onChange} />;
  }
  const definition = FACET_DEFINITIONS.find((item) => item.key === filterKey);
  if (!definition) {
    return null;
  }
  return (
    <FacetValuePanel
      definition={definition}
      options={options?.[filterKey] ?? []}
      selected={filters[filterKey]}
      onChange={(values) => onChange({ ...filters, [filterKey]: values })}
    />
  );
}

function filterIsActive(filterKey: FilterKey, filters: LoanDataExportCandidateFilters): boolean {
  if (filterKey === 'date') {
    return Boolean(filters.dateFrom || filters.dateTo);
  }
  if (filterKey === 'amount') {
    return Boolean(filters.minAmount || filters.maxAmount);
  }
  return filters[filterKey].length > 0;
}

function filterLabel(filterKey: FilterKey, filters: LoanDataExportCandidateFilters): string {
  if (filterKey === 'date') {
    return loanDataExportDateFieldLabel(filters.dateField).replace(' date', '');
  }
  if (filterKey === 'amount') {
    return 'Amount';
  }
  return FACET_DEFINITIONS.find((item) => item.key === filterKey)?.label ?? '';
}

function filterValue(
  filterKey: FilterKey,
  filters: LoanDataExportCandidateFilters,
  options: LoanDataExportFilterOptions | null
): string {
  if (filterKey === 'date') {
    const from = validDate(filters.dateFrom);
    const to = validDate(filters.dateTo);
    if (from && to) {
      const sameMonth = from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth();
      return sameMonth ? format(from, 'MMM yyyy') : `${format(from, 'MMM yyyy')}–${format(to, 'MMM yyyy')}`;
    }
    return from ? `From ${format(from, 'MMM yyyy')}` : to ? `Through ${format(to, 'MMM yyyy')}` : '';
  }
  if (filterKey === 'amount') {
    const minimum = filters.minAmount ? formatAmount(filters.minAmount) : 'any';
    const maximum = filters.maxAmount ? formatAmount(filters.maxAmount) : 'any';
    return `${minimum}–${maximum}`;
  }
  const definition = FACET_DEFINITIONS.find((item) => item.key === filterKey);
  const values = filters[filterKey];
  if (!definition || values.length === 0) {
    return '';
  }
  if (values.length > 1) {
    return `${values.length} values`;
  }
  return options?.[filterKey].find((item) => item.value === values[0])?.label ?? values[0];
}

function clearFilter(filterKey: FilterKey, filters: LoanDataExportCandidateFilters): LoanDataExportCandidateFilters {
  if (filterKey === 'date') {
    return { ...filters, dateFrom: '', dateTo: '' };
  }
  if (filterKey === 'amount') {
    return { ...filters, minAmount: '', maxAmount: '' };
  }
  return { ...filters, [filterKey]: [] };
}

function AppliedFilterChip({
  definition,
  filters,
  options,
  onChange
}: {
  definition: FilterDefinition;
  filters: LoanDataExportCandidateFilters;
  options: LoanDataExportFilterOptions | null;
  onChange: (filters: LoanDataExportCandidateFilters) => void;
}) {
  const [open, setOpen] = useState(false);
  const label = filterLabel(definition.key, filters);
  const value = filterValue(definition.key, filters, options);
  const contentWidth = definition.key === 'date' || definition.key === 'amount' ? 'w-[272px]' : 'w-[270px]';

  return (
    <div className={`inline-flex h-8 min-w-0 max-w-full items-center gap-1.5 rounded-lg border bg-card pl-3 pr-1 ${open ? 'border-primary bg-primary/[0.06]' : ''}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex min-w-0 items-center gap-1.5 border-0 bg-transparent p-0 text-left text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring [line-height:normal]"
            aria-label={`Edit ${label} filter: ${label} · ${value}`}
          >
            <span className="shrink-0 text-muted-foreground">{label}</span>
            <span className="max-w-[170px] truncate font-semibold">{value}</span>
            <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={5}
          collisionPadding={8}
          className={`${contentWidth} rounded-[11px] p-0 shadow-[0_16px_40px_hsl(var(--foreground)/0.16)]`}
        >
          <FilterValuePanel
            filterKey={definition.key}
            filters={filters}
            options={options}
            onChange={onChange}
          />
        </PopoverContent>
      </Popover>
      <button
        type="button"
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-0 bg-transparent p-0 text-muted-foreground outline-none hover:bg-muted hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`Clear ${label} filter`}
        onClick={() => onChange(clearFilter(definition.key, filters))}
      >
        <X className="h-[11px] w-[11px]" strokeWidth={2.6} />
      </button>
    </div>
  );
}

export function LoanCohortFilters({
  filters,
  options,
  loadingOptions,
  optionsError,
  onChange,
  onPasteLoanNumbers
}: LoanCohortFiltersProps) {
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterKey | null>(null);
  const [fieldSelection, setFieldSelection] = useState('__none__');
  const filterCount = countLoanDataExportFilterGroups(filters);
  const activeDefinitions = useMemo(
    () => FILTER_DEFINITIONS.filter((definition) => filterIsActive(definition.key, filters)),
    [filters]
  );

  const handleFilterMenuChange = (open: boolean) => {
    setFilterMenuOpen(open);
    if (!open) {
      setActiveFilter(null);
    }
    setFieldSelection('__none__');
  };

  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-start">
      <div className="flex h-10 w-full shrink-0 items-center gap-2 rounded-lg border border-input bg-card px-3 focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20 lg:w-[26rem]">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          type="text"
          value={filters.search}
          onChange={(event) => onChange({ ...filters, search: event.target.value })}
          placeholder="Search loan number, borrower, officer, investor, program"
          className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-foreground outline-none placeholder:text-muted-foreground [line-height:normal]"
          aria-label="Search export-eligible loans"
        />
        {filters.search ? (
          <button
            type="button"
            aria-label="Clear loan search"
            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => onChange({ ...filters, search: '' })}
          >
            <X className="h-3 w-3" />
          </button>
        ) : null}
      </div>

      <div className="flex min-h-8 min-w-0 flex-1 flex-wrap items-center gap-2 lg:min-h-10" role="group" aria-label="Loan filters">
        {activeDefinitions.map((definition) => (
          <AppliedFilterChip
            key={definition.key}
            definition={definition}
            filters={filters}
            options={options}
            onChange={onChange}
          />
        ))}

        <Popover open={filterMenuOpen} onOpenChange={handleFilterMenuChange}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-dashed border-input bg-transparent px-3 text-sm font-semibold text-primary outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring ${filterMenuOpen ? 'border-primary bg-primary/[0.06]' : ''}`}
              aria-label="Add filter"
              disabled={loadingOptions}
            >
              {loadingOptions
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Plus className="h-3 w-3" strokeWidth={2.6} />}
              Filter
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={5}
            collisionPadding={8}
            className="w-[272px] overflow-hidden rounded-[11px] p-0 shadow-[0_16px_40px_hsl(var(--foreground)/0.16)]"
          >
            {activeFilter ? (
              <div>
                <button
                  type="button"
                  className="flex w-full items-center gap-1.5 border-b bg-transparent px-2.5 py-2 text-left text-xs font-semibold text-muted-foreground outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                  aria-label="Back to filter types"
                  onClick={() => setActiveFilter(null)}
                >
                  <ChevronLeft className="h-3 w-3" />
                  {FILTER_DEFINITIONS.find((definition) => definition.key === activeFilter)?.label}
                </button>
                <FilterValuePanel
                  filterKey={activeFilter}
                  filters={filters}
                  options={options}
                  onChange={onChange}
                />
              </div>
            ) : (
              <Command
                value={fieldSelection}
                onValueChange={setFieldSelection}
                className="rounded-none [&_[data-slot=command-input-wrapper]]:h-10 [&_[data-slot=command-input-wrapper]]:gap-0 [&_[data-slot=command-input-wrapper]]:px-2.5 [&_[data-slot=command-input-wrapper]]:focus-within:ring-0 [&_[data-slot=command-input-wrapper]>svg]:hidden"
              >
                <CommandInput className="text-[13px] [line-height:normal]" placeholder="Filter by…" />
                <CommandList className="max-h-[270px]">
                  <CommandEmpty className="py-6 text-[13px]">No matching filters.</CommandEmpty>
                  <CommandGroup className="p-[5px]">
                    {FILTER_DEFINITIONS.map((definition) => (
                      <CommandItem
                        key={definition.key}
                        value={definition.label}
                        className="min-h-0 rounded-[7px] px-2 py-1.5 text-[13px] [line-height:normal]"
                        onSelect={() => {
                          setActiveFilter(definition.key);
                          setFieldSelection('__none__');
                        }}
                      >
                        <span className="min-w-0 flex-1">{definition.label}</span>
                        {filterIsActive(definition.key, filters) ? (
                          <span className="text-[11.5px] font-semibold text-primary">
                            {definition.key === 'date' || definition.key === 'amount'
                              ? '1'
                              : filters[definition.key].length}
                          </span>
                        ) : null}
                      </CommandItem>
                    ))}
                    {onPasteLoanNumbers ? (
                      <CommandItem
                        value="Paste loan numbers"
                        className="mt-1 min-h-0 border-t px-2 py-2 text-[13px] font-medium [line-height:normal]"
                        onSelect={() => {
                          setFilterMenuOpen(false);
                          onPasteLoanNumbers();
                        }}
                      >
                        Paste loan numbers
                      </CommandItem>
                    ) : null}
                  </CommandGroup>
                </CommandList>
              </Command>
            )}
          </PopoverContent>
        </Popover>

        {filterCount > 0 || filters.search ? (
          <button
            type="button"
            className="h-8 border-0 bg-transparent px-2 text-sm font-medium text-muted-foreground underline outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => onChange(createDefaultLoanDataExportFilters())}
          >
            Clear all
          </button>
        ) : null}
      </div>

      {optionsError ? (
        <div role="alert" className="flex items-center gap-2 text-xs text-destructive lg:basis-full">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {optionsError}
        </div>
      ) : null}
    </div>
  );
}
